/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentUserIsGuest, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import RecentShipmentSummaryCard, {
    RecentShipmentSummaryCardStyles,
} from "@stoneage/App/Components/RecentShipmentSummaryCard/RecentShipmentSummaryCard";
import getUserHasAccessToPageData from "@stoneage/App/Handlers/PageAccess/getUserHasAccessToPageData";
import getRecentShipmentsData from "@stoneage/App/Handlers/RecentShipments/getRecentShipmentsData";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { RecentShipmentItemInfoType, RecentShipmentsInfoType } from "@stoneage/App/Store/Reducers/xRecentShipments";
import {
    getBillToCustomerNumber,
    trackAsImpersonation,
    trackGaMyAccountPagesNavEvent,
} from "@stoneage/App/Utilities/DataLayer";
import React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    titleStyle = "titleStyle",
    numberOfRecords = "numberOfRecords",
    linkText = "linkText",
    url = "url",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titleStyle]: string;
        [fields.numberOfRecords]: number;
        [fields.linkText]: string;
        [fields.url]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;
    const recentShipmentsInfo: RecentShipmentsInfoType = state.xRecentShipments.value;
    const hasAccessToInvoiceHistory = state.xRecentShipments.hasAccessToInvoiceHistory;
    const hasAccessToOrderHistory = state.xRecentShipments.hasAccessToOrderHistory;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
        recentShipmentsInfo,
        orderHistoryPageLink: getPageLinkByPageType(state, "OrderHistoryPage"),
        hasAccessToOrderHistory,
        hasAccessToInvoiceHistory,
        session: getSession(state),
        customerNumber: getBillToCustomerNumber(state),
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {
    getRecentShipmentsData,
    getUserHasAccessToPageData,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentShipmentsStyles {
    widgetContainer?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    tableWrapper?: InjectableCss;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataCell?: InjectableCss;
    recentShipmentItemCard?: RecentShipmentSummaryCardStyles;
}

export const recentShipmentsStyles: RecentShipmentsStyles = {
    titleWrapper: {
        css: css`
            display: flex;
            align-items: baseline;
            margin-bottom: 12px;
        `,
    },
    tableWrapper: {
        css: css`
            width: 100%;
            overflow: auto;
        `,
    },
};

const RecentShipments = ({
    fields,
    isAuthenticated,
    recentShipmentsInfo,
    getRecentShipmentsData,
    orderHistoryPageLink,
    getUserHasAccessToPageData,
    hasAccessToInvoiceHistory,
    hasAccessToOrderHistory,
    session,
    customerNumber,
    impersonation,
}: Props) => {
    const styles = recentShipmentsStyles;
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (isAuthenticated && (!recentShipmentsInfo || Object.keys(recentShipmentsInfo).length === 0)) {
            let hasAccessToOrderHistory = false;
            getUserHasAccessToPageData({
                pageName: "OrderHistoryPage",
                onSuccess: result => {
                    hasAccessToOrderHistory = result?.hasAccess;
                    dispatch({ type: "xRecentShipments/setUserHasAccessToOrderHistoryPage", value: result?.hasAccess });
                    if (hasAccessToOrderHistory) {
                        getRecentShipmentsData({
                            numberOfShipments: fields.numberOfRecords.toString() || "5",
                            onSuccess: result => {
                                dispatch({ type: "xRecentShipments/setValue", value: result });
                            },
                            onError: () => {
                                dispatch({ type: "xRecentShipments/setValue", value: {} });
                            },
                        });

                        getUserHasAccessToPageData({
                            pageName: "InvoiceHistoryPage",
                            onSuccess: result => {
                                dispatch({
                                    type: "xRecentShipments/setUserHasAccessToInvoiceHistoryPage",
                                    value: result?.hasAccess,
                                });
                            },
                            onError: () => {
                                dispatch({
                                    type: "xRecentShipments/setUserHasAccessToInvoiceHistoryPage",
                                    value: false,
                                });
                            },
                        });
                    }
                },
                onError: () => {
                    dispatch({ type: "xRecentShipments/setUserHasAccessToOrderHistoryPage", value: false });
                },
            });
        }
    }, []);

    const { title, titleStyle, url, linkText, openInNewTab } = fields;

    const link = useGetLink(url);

    if (
        !isAuthenticated ||
        recentShipmentsInfo?.recentShipments === undefined ||
        recentShipmentsInfo?.recentShipments?.length === 0 ||
        !hasAccessToOrderHistory
    ) {
        return <></>;
    }

    const recentShipments = recentShipmentsInfo?.recentShipments;

    return (
        <StyledWrapper {...styles.widgetContainer}>
            {title && (
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        variant={titleStyle as keyof ThemeTypography}
                        css={css`
                            text-transform: capitalize;
                            margin-bottom: 0;
                        `}
                    >
                        {title}
                    </Typography>
                    <Link
                        href={link.url}
                        target={openInNewTab ? "_blank" : ""}
                        color="secondary"
                        className="view-all-link"
                        css={css`
                            > span {
                                display: flex;
                                align-items: center;
                                margin-left: 32px;
                                font-size: 15px;
                                font-weight: bold;
                            }
                        `}
                        onClick={() => {
                            link.url &&
                                trackGaMyAccountPagesNavEvent(
                                    "myacct_recentshipment",
                                    link.url,
                                    linkText,
                                    impersonation,
                                    customerNumber,
                                );
                        }}
                    >
                        {linkText}
                        <Icon
                            src="ChevronRight"
                            size={14}
                            color="secondary"
                            css={css`
                                margin-left: 5px;
                            `}
                        ></Icon>
                    </Link>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.tableWrapper} className="recent-shipments-container__wrapper">
                <DataTable {...styles.dataTable} className="mobile-view">
                    <DataTableHead {...styles.dataTableHead} className="card-list-heading">
                        <DataTableHeader title={translate("Shipment Date")} {...styles.dataTableHeaders}>
                            {translate("Shipment Date")}
                        </DataTableHeader>
                        <DataTableHeader title={translate("Invoice Number")} {...styles.dataTableHeaders}>
                            {translate("Invoice Number")}
                        </DataTableHeader>
                        <DataTableHeader title={translate("Invoice Amount")} {...styles.dataTableHeaders}>
                            {translate("Invoice Amount")}
                        </DataTableHeader>
                        <DataTableHeader title={translate("PO Number")} {...styles.dataTableHeaders}>
                            {translate("PO Number")}
                        </DataTableHeader>
                        <DataTableHeader title={translate("Tracking Number")} {...styles.dataTableHeaders}>
                            {translate("Tracking Number")}
                        </DataTableHeader>
                        <DataTableHeader title={translate("Order Number")} {...styles.dataTableHeaders}>
                            {translate("Order Number")}
                        </DataTableHeader>
                        <DataTableHeader title={translate("Order Date")} {...styles.dataTableHeaders}>
                            {translate("Order Date")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody {...styles.dataTableBody} className="card-list-body">
                        {recentShipments.map((recentShipmentItem: RecentShipmentItemInfoType, index: any) => (
                            <DataTableRow
                                {...styles.dataTableRow}
                                data-test-selector="recentOrders_dataLine"
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                            >
                                <RecentShipmentSummaryCard
                                    recentShipmentItem={recentShipmentItem}
                                    extendedStyles={styles.recentShipmentItemCard}
                                    hasAccessToInvoiceHistory={hasAccessToInvoiceHistory}
                                    hasAccessToOrderHistory={hasAccessToOrderHistory}
                                    session={session}
                                />
                            </DataTableRow>
                        ))}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const recentShipments: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentShipments),
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                editorTemplate: "DropDownField",
                defaultValue: "h4",
                fieldType: "General",
                options: [
                    { displayName: "Heading 3", value: "h3" },
                    { displayName: "Heading 4", value: "h4" },
                    { displayName: "Heading 5", value: "h5" },
                    { displayName: "Heading 6", value: "h6" },
                ],
            },
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Shipments Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.linkText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                validate(value: string | null) {
                    if (!value) {
                        return null;
                    }

                    return value.length > 40 ? "Max character allowed is 40" : null;
                },
            },
            {
                name: fields.url,
                displayName: "Link Url",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: false,
            },
        ],
    },
};

export default recentShipments;
